import type { LeaderboardPage, LeaderboardRow } from '../api/rating';
import { rankBadgeUrl, rankName } from './rankInfo';

// The season ladder (D218).
//
// `GET /leaderboard` and the seasons behind it shipped with H5 and NOTHING in the client
// ever rendered them: the whole ranked loop — the reason the rank rail, the promotion
// overlay and the guest-rank machinery exist — ended at a shield on the menu with no board
// behind it. This is the board.
//
// House rules: injected document, a port not a service, every string via textContent
// (T-SEC-006), no default export.

export interface LadderPort {
  load(): Promise<LeaderboardPage>;
}

export interface LadderViewOpts {
  doc: Document;
  onClose(): void;
}

export interface LadderView {
  readonly root: HTMLElement;
  refresh(): Promise<void>;
  dispose(): void;
}

/** What a row is called when the account behind it has gone. Honest, not invented. */
const GONE = 'Player who left';

export function mountLadderView(host: HTMLElement, port: LadderPort, opts: LadderViewOpts): LadderView {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'ladder-view';
  root.dataset['ladderView'] = '1';

  const head = d.createElement('div');
  head.className = 'ladder-head';
  const title = d.createElement('h2');
  title.className = 'ladder-title';
  title.textContent = 'Leaderboard';
  const season = d.createElement('span');
  season.className = 'ladder-season';
  season.dataset['ladderSeason'] = '1';
  const close = d.createElement('button');
  close.type = 'button';
  close.className = 'ladder-close';
  close.dataset['ladderClose'] = '1';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '✕';
  close.addEventListener('click', () => opts.onClose());
  head.append(title, season, close);

  const body = d.createElement('div');
  body.className = 'ladder-body';
  const error = d.createElement('div');
  error.className = 'ladder-error';
  error.dataset['ladderError'] = '1';
  error.hidden = true;

  root.append(head, error, body);
  host.append(root);

  const caps = (text: string): HTMLElement => {
    const l = d.createElement('div');
    l.className = 'caps-label';
    l.textContent = text;
    return l;
  };

  const rowEl = (r: LeaderboardRow): HTMLElement => {
    const el = d.createElement('div');
    el.className = r.you === true ? 'ladder-row ladder-row--you' : 'ladder-row';
    el.dataset['ladderRow'] = String(r.rank);

    const pos = d.createElement('span');
    pos.className = 'ladder-pos';
    pos.textContent = `#${String(r.rank)}`;

    const badge = d.createElement('img');
    badge.className = 'ladder-badge';
    badge.src = rankBadgeUrl(r.tier);
    badge.alt = ''; // the tier is named in text beside it; the shield is decoration
    badge.style.pointerEvents = 'none';

    const name = d.createElement('span');
    name.className = 'ladder-name';
    name.textContent = r.displayName ?? GONE;

    const tier = d.createElement('span');
    tier.className = 'ladder-tier';
    tier.textContent = rankName(r.tier);

    const rating = d.createElement('span');
    rating.className = 'ladder-rating';
    // A provisional number is marked rather than hidden: it is real, it just moves a lot.
    rating.textContent = r.provisional ? `${String(Math.round(r.rating))}?` : String(Math.round(r.rating));

    el.append(pos, badge, name, tier, rating);
    return el;
  };

  const section = (label: string, rows: LeaderboardRow[]): void => {
    if (rows.length === 0) return;
    body.append(caps(label));
    for (const r of rows) body.append(rowEl(r));
  };

  const note = (text: string): HTMLElement => {
    const p = d.createElement('p');
    p.className = 'ladder-note';
    p.textContent = text;
    return p;
  };

  const paint = (page: LeaderboardPage): void => {
    season.textContent = page.season;
    body.replaceChildren();
    if (page.top.length === 0) {
      // An empty board is a real state on a young server, and rendering zero rows silently
      // reads as a broken request. Say which it is.
      body.append(note('Nobody has placed this season yet. Play three rated games and this is where you appear.'));
      return;
    }
    section('This season', page.top);
    // `around` repeats the top rows for anyone already up there — only worth a section when
    // it is actually a different neighbourhood.
    const near = page.around.filter((r) => !page.top.some((t) => t.rank === r.rank));
    section('Around you', near);
    if (page.me === null) body.append(note('You are not on this season’s board yet — three rated games places you.'));
  };

  return {
    root,
    async refresh(): Promise<void> {
      error.hidden = true;
      try {
        paint(await port.load());
      } catch {
        // The board is never worth an error screen; it is worth an honest strip.
        error.hidden = false;
        error.textContent = 'Could not load the leaderboard. Try again in a moment.';
      }
    },
    dispose(): void {
      root.remove();
    },
  };
}
